/**
 * Keeps membership identity and expiry presentation consistent anywhere a
 * business-unit member is shown or edited.
 */
import type { BusinessUnitMemberDto } from 'src/store/generated/leela-api-generated';
import {
  formatUtcDate,
  isUtcDateTimeExpired,
} from 'src/utils/formatDateTime';

interface UserTypePresentation {
  labelKey: 'access_management_external' | 'access_management_internal';
  tagType: 'warning' | 'success';
}

export function getUserTypePresentation (
  userType: BusinessUnitMemberDto['userType'],
): UserTypePresentation {
  return userType === 'EXTERNAL'
    ? { labelKey: 'access_management_external', tagType: 'warning' }
    : { labelKey: 'access_management_internal', tagType: 'success' };
}

interface MembershipExpiryStatusPresentation {
  labelKey: 'status_expired';
  tagType: 'error';
}

interface MembershipExpiryPresentation {
  labelKey: 'access_management_member_expires' | 'edit_user_no_expiry';
  status?: MembershipExpiryStatusPresentation;
  values?: { date: string };
}

export function getMembershipExpiryPresentation (
  expiresAt: string | undefined,
  unavailableLabel: string,
): MembershipExpiryPresentation {
  if (!expiresAt) return { labelKey: 'edit_user_no_expiry' };

  const presentation: MembershipExpiryPresentation = {
    labelKey: 'access_management_member_expires',
    values: { date: formatUtcDate(expiresAt, unavailableLabel) },
  };

  return isUtcDateTimeExpired(expiresAt)
    ? {
        ...presentation,
        status: { labelKey: 'status_expired', tagType: 'error' },
      }
    : presentation;
}
